import { CardIcon, Form, LoopButton, Tooltip, Typography } from '@loophealth/loop-ui-web-library';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ContentEmerald, Dismiss, EditIconGreen, LockIn, Plus } from '../../../assets/img';
import { InfoIcon } from '../../../assets/img/CustomIcons';
import {
    selectModularFamily,
    selectModularSelectedUserBenefits,
    selectModularPurchaseAttributes
} from '../../../redux/slices/ModularEnrolmentSlice/selectors';
import { BenefitPricingStructure } from '../../../redux/slices/PolicyListSlice/types';
import { setPurchaseAttributes } from '../../../redux/slices/ModularEnrolmentSlice';
import theme from '../../../theme';
import {
    capitalizeFirstLetter,
    formatAmount,
    formatFullName,
    getIconMapping,
    textPluralize
} from '../../../utils/common';
import { SEGMENT_ACTIONS } from '../../../utils/constants/SegmentActionConstants';
import useSegment from '../../../utils/hooks/useSegment';
import { ImageView } from '../../atoms/ImageView';
import ProfileIcon from '../../atoms/ProfileIcon';
import { StyledChip } from '../BenefitDetailsModal/styles';
import BenefitPerLifeSelectionModal from './BenefitPerLifeSelectionModal';
import BenefitFamilyDetailModal from './BenefitFamilyDetailModal';
import {
    StyledBenefitCard,
    StyledBenefitCardDisclaimer,
    StyledBenefitDescription,
    StyledBenefitEligibleMembers,
    StyledBenefitPerLifeSelected,
    StyledBenefitPerLifeStatus,
    StyledBenefitsEligibleMember,
    StyledBenefitsEligibleMemberText,
    StyledButton,
    StyledDetails,
    StyledDropDownContainer,
    StyledHeader,
    StyledHeaderText,
    StyledImageContainer,
    StyledLockInCard,
    StyledPremiumAndDisclaimer,
    StyledRow,
    StyledRowBetween,
    StyledRowPremiumPrice,
    StyledDropDown
} from './styles';
import { IBenefitCard } from './types';
import { getDefaultSelectedUserBenefits, getMaxEligibleFamilyMembers } from './utils';
import { FlagsmithFlags, useFeatureFlag } from '../../../utils/flagsmith';

export const BenefitCard: React.FunctionComponent<IBenefitCard> = ({
    plan,
    selectedBenefits,
    handleSelectPlan,
    handleReadMore,
    handleRemovePlan,
    handleUpdate,
    isLastRow
}) => {
    const trackClick = useSegment('click');
    const dispatch = useDispatch();
    const family = useSelector(selectModularFamily).data ?? [];
    const userBenefits = useSelector(selectModularSelectedUserBenefits);
    const [selectedItem, setSelectedItem] = useState('');
    const [selectedPrice, setSelectedPrice] = useState(plan.price);
    const [pseudoSelect, setPseudoSelect] = useState(false);
    const [perLifeModalVisible, setPerLifeModalVisible] = useState(false);
    const [perFamilyModalVisible, setPerFamilyModalVisible] = useState(false);
    const [perLifeRemoveModalVisible, setPerLifeRemoveModalVisible] = useState(false);
    const [isAddingBenefit, setIsAddingBenefit] = useState(false);
    const selected = !!selectedItem || pseudoSelect;
    const isPerLife = plan.pricingStructure === BenefitPricingStructure.PER_LIFE;
    const selectedAttributes = useSelector(selectModularPurchaseAttributes) ?? {};
    const isPerFamily = plan.pricingStructure === BenefitPricingStructure.PER_FAMILY;
    const planIds = useMemo(
        () => (plan.subCategories?.length ? plan.subCategories.map((sb) => sb.id) : [plan.id]),
        [plan]
    );
    const purchaseAttributes = plan.purchaseAttributes ?? {};
    const isPurchaseAttributesEnabled = useFeatureFlag(FlagsmithFlags.PURCHASE_ATTRIBUTES);
    const hasPurchaseAttributes = isPurchaseAttributesEnabled
        ? Object.keys(plan.purchaseAttributes ?? {}).length > 0
        : false;
    const hasTiers = planIds.length > 1;
    const selectedPlanOption = hasTiers ? plan.subCategories?.find((option) => option.id === selectedItem) : plan;
    const maxEligibleMembers = useMemo(() => getMaxEligibleFamilyMembers(plan, family), [plan, family]);
    const selectedMembersCount = useMemo(
        () => Object.keys(getDefaultSelectedUserBenefits(plan, userBenefits)).length,
        [plan, userBenefits]
    );
    const clearUnselectedPurchaseAttributes = (id: string) => {
        const remainingAttributes = Object.keys(selectedAttributes).reduce((acc, key) => {
            if (key !== id) {
                acc[key] = selectedAttributes[key];
            }
            return acc;
        }, {});
        dispatch(setPurchaseAttributes(remainingAttributes));
    };
    const handleSelect = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        let clickAction = SEGMENT_ACTIONS.CLICK.REMOVE_BENEFIT;
        if (pseudoSelect && !selectedItem) setPseudoSelect(false);
        else if (selectedItem) {
            if (isPerLife) {
                setPerLifeRemoveModalVisible(true);
            } else {
                handleRemovePlan(selectedItem);
                clearUnselectedPurchaseAttributes(selectedItem);
                setSelectedItem('');
                setPseudoSelect(false);
            }
        } else {
            clickAction = SEGMENT_ACTIONS.CLICK.ADD_BENEFIT;
            if (isPerLife) {
                setPerLifeModalVisible(true);
            } else {
                if (hasTiers) {
                    setPseudoSelect(true);
                } else {
                    handleSingleBenefit();
                }
            }
        }
        trackClick({
            name: clickAction.name,
            properties: {
                ...clickAction.properties,
                benefit_name: plan.name,
                benefit_id: plan.id
            }
        });
    };

    const handleSingleBenefit = () => {
        const id = plan.id;
        handleSelectPlan(id);
        setSelectedItem(id);
        if (hasPurchaseAttributes) {
            setPerFamilyModalVisible(true);
        }
    };

    const handleModalClose = () => {
        setPerFamilyModalVisible(false);
        // If we were in the process of adding and the user cancelled, reset selection
        if (isAddingBenefit && !selectedItem) {
            setPseudoSelect(false);
            setIsAddingBenefit(false);
        }
    };

    const handleClick = (e: string) => {
        const clickAction = SEGMENT_ACTIONS.CLICK.SELECT_PLAN;
        const tier = plan.subCategories?.find((subCategory) => subCategory.id === e);
        const benefitSelectedAttributes = selectedAttributes[selectedItem] ?? null;
        trackClick({
            name: clickAction.name,
            properties: {
                ...clickAction.properties,
                benefit_id: e,
                benefit_name: tier?.name,
                benefit_premium: tier?.price,
                benefit_coverage: tier?.sumInsured
            }
        });
        if (handleUpdate) {
            handleUpdate(selectedItem, e);
        } else {
            handleSelectPlan(e);
        }
        if (!benefitSelectedAttributes) {
            setPerFamilyModalVisible(true);
        } else {
            const selectedPurchaseAttributes = structuredClone(selectedAttributes);
            delete selectedPurchaseAttributes[selectedItem];
            const updatedAttributes = { ...selectedPurchaseAttributes, [e]: benefitSelectedAttributes };
            dispatch(setPurchaseAttributes(updatedAttributes));
        }
    };

    const handleOnCardClick = (e: React.MouseEvent) => {
        if (e.isPropagationStopped() || e.isDefaultPrevented()) return;
        handleReadMore(plan);
    };

    const handleOnDropdownClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
    };

    const handleEdit = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        setPerLifeModalVisible(true);
    };

    const handleEditPurchaseAttributes = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        setPerFamilyModalVisible(true);
    };

    const subCategoryOptions = useMemo(() => {
        return plan.subCategories?.map((subCategory) => {
            return { name: `${subCategory.name} - ${formatAmount(subCategory.price)}`, value: subCategory.id };
        });
    }, [plan.subCategories, selectedBenefits]);
    useEffect(() => {
        if (selectedBenefits.includes(plan.id) && !hasTiers) {
            setSelectedItem(plan.id);
            setSelectedPrice(plan.price * (userBenefits[plan.id]?.length ?? (isPerLife ? 0 : 1)));
        } else {
            const selectedSubCategories = plan?.subCategories?.filter((subCategory) =>
                selectedBenefits.includes(subCategory.id)
            );
            if (selectedSubCategories.length) {
                const price = selectedSubCategories.reduce(
                    (acc, curr) => acc + curr.price * (userBenefits[curr.id]?.length ?? (isPerLife ? 0 : 1)),
                    0
                );
                setSelectedPrice(price);
                setSelectedItem(selectedSubCategories[0].id);
            } else setSelectedPrice(plan.price);
        }
    }, [selectedBenefits, plan, userBenefits]);
    return (
        <>
            <StyledBenefitCard id={plan.id} selected={selected}>
                <StyledDetails>
                    <StyledHeader>
                        <StyledHeader>
                            <CardIcon imageUrl={plan.logo} />
                            <StyledHeaderText>
                                <Typography variant={'large'} weight={'semiBold'}>
                                    {plan.name}
                                </Typography>
                                {!!plan.sumInsured && (
                                    <Typography variant="extraSmall" weight="semiBold">
                                        {hasTiers ? 'Starting coverage ' : ''}
                                        {formatAmount(plan.sumInsured)}
                                    </Typography>
                                )}
                            </StyledHeaderText>
                        </StyledHeader>
                        <div>
                            <LoopButton
                                borderColor={theme.colors.loopEmerald}
                                fontWeight={'semiBold'}
                                size={'small'}
                                variant="text"
                                onClick={handleSelect}
                            >
                                <StyledButton>
                                    <StyledImageContainer src={selected ? Dismiss : Plus} height="16px" width="16px" />
                                    <Typography variant={'extraSmall'}>
                                        {selected ? 'Remove' : 'Add Benefit'}
                                    </Typography>
                                </StyledButton>
                            </LoopButton>
                        </div>
                    </StyledHeader>
                    <StyledBenefitDescription>
                        <Typography variant={'small'}>{plan.description}</Typography>
                        <LoopButton
                            variant="text"
                            fontWeight="semiBold"
                            onClick={handleOnCardClick}
                            iconSrc={ContentEmerald}
                        >
                            PLAN DETAILS&nbsp;&nbsp;
                        </LoopButton>
                    </StyledBenefitDescription>
                    {plan.lockIn && (
                        <StyledLockInCard>
                            <StyledChip>
                                <ImageView src={LockIn} height="20px" width="20px" />
                                <Typography variant={'extraSmall'} weight={'semiBold'}>
                                    {plan.lockIn}
                                </Typography>
                            </StyledChip>
                        </StyledLockInCard>
                    )}
                </StyledDetails>
                <StyledRowBetween>
                    <StyledPremiumAndDisclaimer data-testid="ala-carte-benefit-card-premium">
                        <StyledRowPremiumPrice
                            selected={selected}
                            $forceRow={selected && !isPerLife && !hasTiers && !hasPurchaseAttributes}
                        >
                            <Typography variant={'extraSmall'}>
                                {selected ? 'Premium per year to pay' : 'Premium per year from'}
                            </Typography>
                            {(!selectedItem || (!isPerLife && !hasTiers && !hasPurchaseAttributes)) && (
                                <Typography variant={'extraSmall'} weight={'semiBold'}>
                                    <Typography variant={'title2'} weight={'semiBold'}>
                                        {formatAmount(selectedPrice)}
                                    </Typography>
                                    {isPerLife ? '/member' : '/family'}
                                </Typography>
                            )}
                        </StyledRowPremiumPrice>
                    </StyledPremiumAndDisclaimer>
                    {isPerLife ? (
                        selected ? (
                            <StyledBenefitPerLifeSelected onClick={handleEdit}>
                                <Typography variant="small" weight="medium">
                                    {selectedMembersCount} {textPluralize('member', selectedMembersCount)} -{' '}
                                    {formatAmount(selectedPrice)}/yr
                                </Typography>
                                <img src={EditIconGreen} />
                            </StyledBenefitPerLifeSelected>
                        ) : (
                            !plan.disclaimer && (
                                <StyledBenefitPerLifeStatus>
                                    <Typography variant="small" weight="semiBold">
                                        {maxEligibleMembers.length} {textPluralize('member', maxEligibleMembers.length)}
                                        &nbsp;
                                        <Typography variant="small">are eligible for this</Typography>
                                    </Typography>
                                    <Tooltip
                                        text={
                                            <StyledBenefitEligibleMembers>
                                                <Typography variant="extraSmall" color="tertiary">
                                                    Eligible Members:
                                                </Typography>
                                                {maxEligibleMembers.map((m) => (
                                                    <StyledBenefitsEligibleMember key={m.id}>
                                                        <ProfileIcon
                                                            icon={getIconMapping(m.relationship, m.gender)}
                                                            bgColor={theme.colors.coverYellow}
                                                        />
                                                        <StyledBenefitsEligibleMemberText>
                                                            <Typography
                                                                variant="medium"
                                                                weight="semiBold"
                                                                color="tertiary"
                                                            >
                                                                {formatFullName(m.firstName)}
                                                            </Typography>
                                                            <Typography
                                                                variant="extraSmall"
                                                                weight="medium"
                                                                color="tertiary"
                                                            >
                                                                {capitalizeFirstLetter(m.relationship)}
                                                            </Typography>
                                                        </StyledBenefitsEligibleMemberText>
                                                    </StyledBenefitsEligibleMember>
                                                ))}
                                            </StyledBenefitEligibleMembers>
                                        }
                                        beak="top-center"
                                        type="informative"
                                    >
                                        <InfoIcon width="20" height="20" strokeColor={theme.colors.secondaryText} />
                                    </Tooltip>
                                </StyledBenefitPerLifeStatus>
                            )
                        )
                    ) : selected ? (
                        hasPurchaseAttributes && selectedItem ? (
                            <StyledBenefitPerLifeSelected
                                onClick={handleEditPurchaseAttributes}
                                data-testid="ala-carte-benefit-card-per-family-selected"
                            >
                                <Typography variant="small" weight="medium">
                                    {formatAmount(selectedPrice)}/yr
                                </Typography>
                                <img src={EditIconGreen} />
                            </StyledBenefitPerLifeSelected>
                        ) : (
                            hasTiers &&
                            (!selectedItem || handleUpdate) && (
                                <StyledRow>
                                    <StyledDropDownContainer data-testid="ala-carte-benefit-card-dropdown-container">
                                        <Typography variant={'small'} weight={'medium'}>
                                            {'Choose your Plan'}
                                        </Typography>
                                    </StyledDropDownContainer>
                                    <StyledDropDown
                                        onClick={handleOnDropdownClick}
                                        data-testid="ala-carte-benefit-card-dropdown"
                                    >
                                        <Form.Dropdown
                                            placeholder="Select Plan"
                                            listItems={subCategoryOptions}
                                            onClick={(e: string) => handleClick(e)}
                                            selectedItem={selectedItem}
                                            autoDisplayItems={!selectedItem}
                                            onClickOutside={() => {
                                                if (pseudoSelect) setPseudoSelect(false);
                                            }}
                                            direction={isLastRow ? 'top' : 'bottom'}
                                        />
                                    </StyledDropDown>
                                </StyledRow>
                            )
                        )
                    ) : null}
                    {!selected && plan.disclaimer && (
                        <StyledBenefitCardDisclaimer>
                            <div>
                                <InfoIcon width="20" height="20" strokeColor={theme.colors.secondaryText} />
                            </div>
                            <Typography variant="small">{plan.disclaimer}</Typography>
                        </StyledBenefitCardDisclaimer>
                    )}
                </StyledRowBetween>
            </StyledBenefitCard>
            {isPerLife && (
                <BenefitPerLifeSelectionModal
                    planIds={planIds}
                    plan={plan}
                    isVisible={perLifeModalVisible}
                    setIsVisible={setPerLifeModalVisible}
                    userBenefits={userBenefits}
                    family={family}
                    deletionModalVisible={perLifeRemoveModalVisible}
                    setDeletionModalVisible={setPerLifeRemoveModalVisible}
                    setSelectedItem={setSelectedItem}
                />
            )}
            {selectedPlanOption && (
                <BenefitFamilyDetailModal
                    isVisible={isPerFamily && hasPurchaseAttributes && perFamilyModalVisible}
                    setIsVisible={handleModalClose}
                    plan={plan}
                    family={family}
                    planIds={planIds}
                    handleSelectPlan={handleSelectPlan}
                    removeSelectPlan={handleRemovePlan}
                    purchaseAttributes={purchaseAttributes}
                    setSelectedItem={setSelectedItem}
                    setSelectedPrice={setSelectedPrice}
                    handleUpdate={handleUpdate}
                    selectedItem={selectedItem}
                    selectedAttributes={selectedAttributes}
                    selectedPlanOption={selectedPlanOption}
                    selectedBenefits={selectedBenefits}
                />
            )}
        </>
    );
};

export default BenefitCard;
